import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import './ProjectOpenItemForm.css';
import Dropdown from 'components/shared/dropdown/Dropdown';
import MultipleDropdown from 'components/shared/dropdown/MultipleDropdown';
import TextareaAutosize from 'react-autosize-textarea';
import DatetimePicker from 'components/shared/datetime-picker/DatetimePicker';
import { todayValidation } from 'services/utils/date-util';
import uploadSvg from 'images/upload.svg';
import removeIconPng from 'images/remove.png';
import Dropzone from 'react-dropzone';
import DocumentLink from 'components/shared/document/DocumentLink';
import ImageUpload from 'components/shared/image-upload/ImageUpload';
import { imageExtensions } from 'services/utils/files-util';
import LabeledCheckBox from 'components/shared/checkbox/LabeledCheckBox';
import Button from 'components/shared/button/Button';
import { Enterprise } from 'domain/enterprise';
import { Project } from 'domain/project';
import { getCurrentProject } from 'components/admin/projects/details/store/selectors';
import { getDirectory } from 'components/admin/projects/details/project-directory/store/selectors';
import {
  getCritical,
  getOpenItemForm,
  getOpenItemFormErrors,
} from 'components/admin/projects/details/project-open-items/store/selectors';
import { loadDirectoryRequest } from 'components/admin/projects/details/project-directory/store/actions';
import {
  setOpenItemFormAttr,
  setOpenItemFormErrors,
  clearOpenItemFormErrorAttr,
  resetOpenItemForm,
} from 'components/admin/projects/details/project-open-items/store/actions';

class ProjectOpenItemForm extends Component {
  static propTypes = {
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
  };

  componentDidMount() {
    this.loadDirectory();
  }

  componentWillUnmount() {
    this.props.dispatch(resetOpenItemForm());
  }

  loadDirectory = () => {
    const { currentProject, directory, dispatch } = this.props;
    if (!directory || directory.project_id !== currentProject.id) {
      dispatch(loadDirectoryRequest(currentProject.id));
    }
  };

  handleAttributeChange = (attr, value) => {
    this.props.dispatch(setOpenItemFormAttr(attr, value));
    this.props.dispatch(clearOpenItemFormErrorAttr(attr));
  };

  mapRecipientIds = () => {
    const { form } = this.props;
    return form.recipients && form.recipients.map((rcpt) => rcpt[0]);
  };

  handleSaveDraft = () => {
    const { form, critical, onSave } = this.props;
    const recipientIds = this.mapRecipientIds();
    const values = { ...form, recipientIds, critical, draft: true };
    onSave(values);
  };

  handleSave = () => {
    const { form, critical, onSave, dispatch } = this.props;
    const errors = {};
    if (
      form.projectContributorId === null ||
      form.projectContributorId === undefined
    ) {
      errors.projectContributorId = true;
    }

    if (!form.assigneeId) {
      errors.assigneeId = true;
    }

    if (!form.resolveBy) {
      errors.resolveBy = true;
    }

    if (!form.description) {
      errors.description = true;
    }

    if (Object.keys(errors).length > 0) {
      dispatch(setOpenItemFormErrors(errors));
    } else {
      const recipientIds = this.mapRecipientIds();
      const values = { ...form, recipientIds, critical, draft: false };
      onSave(values);
    }
  };

  projectContributorOptions = () => {
    const options = Project.projectContributorOptions(this.props);
    options.unshift({ value: '', label: '00 General Contractor' });
    return options;
  };

  recipientOptions = () => {
    const { currentProject } = this.props;
    return Enterprise.spackleOrGc(currentProject.contributor_type)
      ? Project.recipientOptions(this.props)
      : Project.primaryRecipientOptions(this.props);
  };

  assigneeOptions = () => {
    return this.recipientOptions().map((opt) => {
      return { value: opt.id, label: opt.label };
    });
  };

  getArrayAttribute = (attr) => {
    const { form } = this.props;
    if (form && form[attr] && form[attr].length > 0) {
      return form[attr];
    }
    return [];
  };

  setImages = (files) => {
    const images = [...this.getArrayAttribute('images')];
    files.forEach(function (val) {
      images.push({
        url: URL.createObjectURL(val),
        file: val,
      });
    });

    this.handleAttributeChange('images', images);
  };

  removeImage = (index) => {
    const images = [...this.getArrayAttribute('images')];
    const deletedImages = [...this.getArrayAttribute('deletedImages')];
    const deletedImage = images.splice(index, 1);
    deletedImages.push(deletedImage[0]);

    this.handleAttributeChange('images', images);
    this.handleAttributeChange('deletedImages', deletedImages);
  };

  setDocuments = (files) => {
    const docs = [...this.getArrayAttribute('documents')];
    files.forEach(function (val) {
      docs.push({
        file: val,
        name: val.name,
      });
    });

    this.handleAttributeChange('documents', docs);
  };

  removeDocument = (index) => {
    const docs = [...this.getArrayAttribute('documents')];
    const deletedDocs = [...this.getArrayAttribute('deletedDocuments')];
    const deletedDoc = docs.splice(index, 1);
    deletedDocs.push(deletedDoc[0]);

    this.handleAttributeChange('documents', docs);
    this.handleAttributeChange('deletedDocuments', deletedDocs);
  };

  showSaveDraftBtn = () => {
    const { form } = this.props;
    return !form.id || form.draft;
  };

  render() {
    const { form, errors, onCancel } = this.props;
    const showSaveDraftBtn = this.showSaveDraftBtn();

    return (
      <div className="project-open-item-form">
        {form.id && (
          <div className="project-open-item-form-block">
            <label className="project-open-item-form-label project-open-item-resolved-label">
              Mark as Resolved
            </label>
            <LabeledCheckBox
              onChange={this.handleAttributeChange.bind(null, 'resolved')}
              checked={form.resolved}
            />
          </div>
        )}
        <div className="project-open-item-form-block csi-code-block">
          <label className="project-open-item-form-label">CSI Code</label>
          <Dropdown
            className={classnames({
              'project-open-item-form-error-field': errors.projectContributorId,
            })}
            placeholder="Select CSI Code..."
            onChange={(option) =>
              this.handleAttributeChange(
                'projectContributorId',
                option && option.value
              )
            }
            value={form.projectContributorId}
            options={this.projectContributorOptions()}
          />
        </div>
        <div className="project-open-item-form-block assignee-block">
          <label className="project-open-item-form-label">Assigned To</label>
          <Dropdown
            className={classnames({
              'project-open-item-form-error-field': errors.assigneeId,
            })}
            placeholder="Select Assignee..."
            onChange={(option) =>
              this.handleAttributeChange('assigneeId', option.value)
            }
            value={form.assigneeId}
            options={this.assigneeOptions()}
          />
        </div>
        <div className="project-open-item-form-block recipients-block">
          <label className="project-open-item-form-label">
            CC Recipient(s)
          </label>
          <MultipleDropdown
            options={this.recipientOptions()}
            currentValues={form.recipients || []}
            placeholder="Select Contributors to CC"
            onChange={(values) =>
              this.handleAttributeChange('recipients', values)
            }
          />
        </div>
        <div className="project-open-item-form-block resolve-by-block">
          <label className="project-open-item-form-label">Due Date</label>
          <div
            className={classnames({
              'project-open-item-form-error-field': errors.resolveBy,
            })}
          >
            <DatetimePicker
              isValidDate={todayValidation}
              placeholder="Resolve By"
              onChange={(value) =>
                this.handleAttributeChange('resolveBy', value)
              }
              value={form.resolveBy}
            />
          </div>
        </div>
        <div className="project-open-item-form-block">
          <label className="project-open-item-form-label">Description</label>
          <TextareaAutosize
            rows={2}
            className={classnames({
              'text-area': true,
              'input-area': true,
              'form-control': true,
              'project-open-item-form-error-field': errors.description,
            })}
            placeholder="Enter Text Here..."
            onBlur={(e) =>
              this.handleAttributeChange('description', e.target.value)
            }
            defaultValue={form.description}
          />
        </div>
        <div className="project-open-item-form-block project-open-item-form-block-docs">
          {form.documents &&
            form.documents.map((doc, index) => {
              return (
                <div className="doc-viewer" key={index}>
                  <img
                    className="remove-doc-icon"
                    src={removeIconPng}
                    alt="remove"
                    onClick={() => this.removeDocument(index)}
                  />
                  <DocumentLink file={doc} url={doc.url} />
                </div>
              );
            })}
          <Dropzone
            accept="application/pdf, .doc, .docx, .xls, .xlsx"
            className="dropzone-container"
            onDrop={(files) => this.setDocuments(files)}
          >
            <span>Please drag and drop any file into this area</span>
            <img src={uploadSvg} alt="upload" />
          </Dropzone>
        </div>
        <div className="project-open-item-form-block">
          <ImageUpload
            extensions={imageExtensions}
            multiple
            onUpload={(files) => this.setImages(files)}
            onRemove={(index) => this.removeImage(index)}
            images={form.images}
          />
        </div>
        <div className="buttons-container">
          {showSaveDraftBtn && (
            <Button
              label="Save Draft"
              className="button-white"
              onClick={this.handleSaveDraft}
            />
          )}
          <Button
            label={showSaveDraftBtn ? 'Submit' : 'Save'}
            className="button-orange"
            onClick={this.handleSave}
          />
          <Button label="Cancel" className="button-gray" onClick={onCancel} />
        </div>
      </div>
    );
  }
}

export default connect((state) => {
  return {
    currentProject: getCurrentProject(state),
    directory: getDirectory(state),
    critical: getCritical(state),
    form: getOpenItemForm(state),
    errors: getOpenItemFormErrors(state),
  };
})(ProjectOpenItemForm);



// WEBPACK FOOTER //
// ./src/components/admin/projects/details/project-open-items/ProjectOpenItemForm.js